/**
 * Strava API Integration Service
 * Handles Strava OAuth, connection status, and sync management.
 *
 * Mirrors the shape and patterns of garminService.ts — the two integrations
 * are designed as siblings. See /web/src/services/garminService.ts for the
 * reference implementation.
 */

import { storage } from './storage';
import { API_BASE_URL } from '../utils/api';

export interface StravaConnectionStatus {
  connected: boolean;
  strava_available?: boolean; // Strava light switch — false when current user's tier doesn't have access (awaiting approval or non-tester)
  athlete_id?: string | number;
  athlete_name?: string;
  profile_image_url?: string;
  connected_at?: string;
  last_sync_at?: string;
  city?: string;
  state?: string;
  country?: string;
  error?: string;
}

export interface StravaAuthResponse {
  authorization_url: string;
  state?: string;
  instructions?: string;
}

export interface StravaQueueStatus {
  queued: boolean;
  position: number;
  wait_minutes: number;
}

export interface StravaSyncStatus {
  overall_status: string;
  progress_percentage?: number;
  total_activities: number;
  errors?: number;
  elapsed_seconds: number;
}

/**
 * Strava write-back preferences (2026-05-22 sprint).
 *
 * Per-user opt-in toggles for what Cedar may do when writing back to the
 * user's Strava account. Mirrors the 5 columns added in migration 1547.
 *
 * Defaults are conservative on goal-sharing (FALSE) because goals can be
 * sensitive (training for a race ≠ wanting weight-loss goals visible).
 * All other toggles default TRUE.
 */
export interface StravaPreferences {
  /** Master switch — when false, Cedar reads from Strava for coaching but never writes back. */
  strava_writeback_enabled: boolean;
  /** Append "— Coached by Cedar" suffix to activity titles. */
  strava_rename_title: boolean;
  /** Attach a branded stats card image (v1: disabled product-wide; toggle still surfaced for v2). */
  strava_attach_stats_card: boolean;
  /** Allow Cedar to reference your training goal in the public Strava description. Default OFF. */
  strava_share_goal_context: boolean;
  /** Allow Cedar to include comparative metrics ("fastest 5k in 8 weeks") in the Strava description. */
  strava_share_progress_context: boolean;
}

// Authentication helper
const getAuthHeaders = async () => {
  const token = await storage.getItem('jwt_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

class StravaService {
  /**
   * Check if user has connected Strava account.
   * Returns strava_available as the light switch state.
   */
  async getConnectionStatus(): Promise<StravaConnectionStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/strava/status`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to get connection status');
      }

      return await response.json();
    } catch (error) {
      // console.warn (not .error) to avoid LogBox red screen for routine
      // network-unreachable situations (simulator offline, backend down, etc.)
      console.warn('Failed to get Strava connection status:', error);
      return { connected: false };
    }
  }

  /**
   * Get Strava OAuth authorization URL.
   *
   * @param platform - 'ios' or 'android' to request mobile OAuth URL
   *                   (routes to Strava's /oauth/mobile/authorize which
   *                   deep-links into the Strava app if installed)
   */
  async getAuthUrl(platform?: 'ios' | 'android'): Promise<StravaAuthResponse> {
    try {
      const qs = platform ? `?platform=${platform}` : '';
      const response = await fetch(`${API_BASE_URL}/api/auth/strava/connect${qs}`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to get auth URL');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get Strava auth URL:', error);
      throw error;
    }
  }

  /**
   * Initiate Strava OAuth connection.
   * On web: opens Strava auth page in a popup window.
   * On native: the caller should use Linking or an in-app browser session;
   * this helper is primarily for web.
   */
  async connect(): Promise<void> {
    try {
      const authData = await this.getAuthUrl();

      // Web popup flow — native handles this via Linking.openURL in the caller
      if (typeof window !== 'undefined' && window.open) {
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const authWindow = window.open(
          authData.authorization_url,
          'Strava Authorization',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
        );

        // Listen for window close to refresh status
        const checkCallback = setInterval(() => {
          try {
            if (authWindow && authWindow.closed) {
              clearInterval(checkCallback);
              if (typeof window.location?.reload === 'function') {
                window.location.reload();
              }
            }
          } catch (e) {
            // Cross-origin error expected while auth window is on strava.com
          }
        }, 500);
      }
    } catch (error) {
      console.error('Failed to initiate Strava connection:', error);
      throw error;
    }
  }

  /**
   * Disconnect Strava account.
   * Backend calls Strava's /oauth/deauthorize API, removes webhook subscription,
   * and soft-deletes the strava_tokens row (is_active = FALSE).
   */
  async disconnect(): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/strava/disconnect`, {
        method: 'POST',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to disconnect');
      }
    } catch (error) {
      console.error('Failed to disconnect Strava:', error);
      throw error;
    }
  }

  /**
   * Get Strava initial-sync queue position for the current user.
   * Matches GET /api/auth/garmin/queue-status for frontend parity.
   */
  async getQueueStatus(): Promise<StravaQueueStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/strava/queue-status`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to get queue status');
      }

      return await response.json();
    } catch (error) {
      console.warn('Failed to get Strava queue status:', error);
      return { queued: false, position: 0, wait_minutes: 0 };
    }
  }

  /**
   * Check Strava sync progress for the current user.
   * Matches GET /api/auth/garmin/check-sync-status for frontend parity.
   */
  async checkSyncStatus(): Promise<StravaSyncStatus> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/strava/check-sync-status`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to check sync status');
      }

      return await response.json();
    } catch (error) {
      console.warn('Failed to check Strava sync status:', error);
      return { overall_status: 'error', total_activities: 0, elapsed_seconds: 0 };
    }
  }

  /**
   * Get the user's Strava write-back preferences (the 5 opt-in toggles).
   *
   * Backend reads from user_core_profile (migration 1547 columns) and
   * applies defaults if no row exists. Frontend should call this every
   * time the StravaPreferencesDrawer opens so the toggle state is fresh.
   */
  async getPreferences(): Promise<StravaPreferences> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/strava/preferences`, {
        method: 'GET',
        headers: await getAuthHeaders(),
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.warn('Failed to fetch Strava preferences:', error);
      // Surface defaults rather than throwing — the drawer can still render
      // with the conservative defaults if the API errs transiently.
      return {
        strava_writeback_enabled: true,
        strava_rename_title: true,
        strava_attach_stats_card: true,
        strava_share_goal_context: false,
        strava_share_progress_context: true,
      };
    }
  }

  /**
   * Update one or more Strava write-back preferences. Partial body OK —
   * pass only the fields that changed. Returns the full updated state.
   *
   * Toggling `strava_writeback_enabled` OFF immediately cancels any
   * pending Strava writeback queue items on the backend so the opt-out
   * takes effect within seconds, not the ~1 minute it would take for
   * the queue processor's per-row check to catch up.
   */
  async updatePreferences(updates: Partial<StravaPreferences>): Promise<StravaPreferences> {
    const response = await fetch(`${API_BASE_URL}/api/auth/strava/preferences`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Failed to update Strava preferences (${response.status}): ${body}`);
    }
    return await response.json();
  }
}

export default new StravaService();
